import { ContractsValueObjectsAmountStrategyTypeEnum } from 'generated/resources/graphql'
import { z } from 'zod'

const AMOUNT_STRATEGY_TYPE_VALUES = [
  'contracts_fixed_amount_strategy',
  'contracts_variable_amount_strategy',
] as const

const RECURRENCE_VALUES = [
  'monthly',
  'every_2_months',
  'every_3_months',
  'every_4_months',
  'every_5_months',
  'every_6_months',
  'every_7_months',
  'every_8_months',
  'every_9_months',
  'every_10_months',
  'every_11_months',
  'every_12_months',
] as const

const FIXED_AMOUNT_STRATEGY = 'contracts_fixed_amount_strategy'
const VARIABLE_AMOUNT_STRATEGY = 'contracts_variable_amount_strategy'
const PER_WORKED_DAY_STRATEGY = 'contracts_per_worked_day_strategy'
const SHOW_PENDING_PER_WORKED_DAY_FIELDS = false
const DURATION_FIELD_UNITS: Array<'hours' | 'minutes'> = ['hours', 'minutes']

type PendingAmountStrategyType = typeof PER_WORKED_DAY_STRATEGY

type AdditionalCompensationWizardAmountStrategyType =
  | ContractsValueObjectsAmountStrategyTypeEnum
  | PendingAmountStrategyType

type BasicContext = {
  amountStrategyType?: AdditionalCompensationWizardAmountStrategyType
  payrollConceptId?: string
}

type BasicStepValues = {
  payrollConceptId: string
  amountStrategyType: AdditionalCompensationWizardAmountStrategyType
  recurrence: (typeof RECURRENCE_VALUES)[number]
  firstPaymentOn: Date
}

type PendingPerWorkedDayDefinitionValues = {
  amountPerDay?: number | undefined
  daysCountedFrom?: 'previous_month' | undefined
  minimumHoursPerDay?: number | undefined
  workLocations?: string[] | undefined
  includedAbsences?: string[] | undefined
  eligibleDays?: string[] | undefined
}

type DefinitionStepValues = {
  fixedAmount?: number | undefined
  maximumAmountPerCycle?: number | null | undefined
  pendingPerWorkedDayFields?: PendingPerWorkedDayDefinitionValues
}

type RawDefinitionStepValues = Omit<
  DefinitionStepValues,
  'fixedAmount' | 'maximumAmountPerCycle'
> & {
  fixedAmount?: number | string | undefined
  maximumAmountPerCycle?: number | string | null | undefined
}

const normalizeDefinitionNumber = (
  value: number | string | null | undefined
): number | null | undefined => {
  if (value == null || value === '') return undefined

  return typeof value === 'string' ? Number(value) : value
}

const durationSecondsToMinutesSchema = z.number().transform((seconds) => seconds / 60)

const optionalDurationSecondsToMinutesSchema = z
  .number()
  .nullable()
  .optional()
  .transform((seconds) => (seconds == null ? seconds : seconds / 60))

const DURATION_FIELDS = {
  hours: {
    maxVisibleDigits: 5,
  },
  minutes: {
    maxVisibleDigits: 10,
  },
}

const isNumber = (value: unknown): value is number => typeof value === 'number'

const isString = (value: unknown): value is string => typeof value === 'string'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isPendingPerWorkedDayDefinitionValues = (
  value: unknown
): value is PendingPerWorkedDayDefinitionValues => isRecord(value)

const isRecurrenceValue = (value: unknown): value is (typeof RECURRENCE_VALUES)[number] =>
  isString(value) && RECURRENCE_VALUES.some((recurrence) => recurrence === value)

const isAdditionalCompensationWizardAmountStrategyType = (
  value: unknown
): value is AdditionalCompensationWizardAmountStrategyType =>
  value === FIXED_AMOUNT_STRATEGY ||
  value === VARIABLE_AMOUNT_STRATEGY ||
  value === PER_WORKED_DAY_STRATEGY

const isBasicStepValues = (value: unknown): value is BasicStepValues =>
  isRecord(value) &&
  isString(value.payrollConceptId) &&
  isAdditionalCompensationWizardAmountStrategyType(value.amountStrategyType) &&
  isRecurrenceValue(value.recurrence) &&
  value.firstPaymentOn instanceof Date

const isOptionalNumericField = (value: unknown) =>
  value == null || value === '' || isNumber(value) || isString(value)

const isRawDefinitionStepValues = (value: unknown): value is RawDefinitionStepValues =>
  isRecord(value) &&
  isOptionalNumericField(value.fixedAmount) &&
  isOptionalNumericField(value.maximumAmountPerCycle) &&
  (value.pendingPerWorkedDayFields === undefined ||
    isPendingPerWorkedDayDefinitionValues(value.pendingPerWorkedDayFields))

const isSupportedAmountStrategyType = (
  amountStrategyType: AdditionalCompensationWizardAmountStrategyType
): amountStrategyType is ContractsValueObjectsAmountStrategyTypeEnum =>
  amountStrategyType === FIXED_AMOUNT_STRATEGY || amountStrategyType === VARIABLE_AMOUNT_STRATEGY

export {
  AMOUNT_STRATEGY_TYPE_VALUES,
  DURATION_FIELDS,
  DURATION_FIELD_UNITS,
  FIXED_AMOUNT_STRATEGY,
  PER_WORKED_DAY_STRATEGY,
  RECURRENCE_VALUES,
  SHOW_PENDING_PER_WORKED_DAY_FIELDS,
  VARIABLE_AMOUNT_STRATEGY,
  durationSecondsToMinutesSchema,
  isAdditionalCompensationWizardAmountStrategyType,
  isBasicStepValues,
  isNumber,
  isPendingPerWorkedDayDefinitionValues,
  isRawDefinitionStepValues,
  isSupportedAmountStrategyType,
  normalizeDefinitionNumber,
  optionalDurationSecondsToMinutesSchema,
}

export type {
  AdditionalCompensationWizardAmountStrategyType,
  BasicContext,
  BasicStepValues,
  DefinitionStepValues,
  PendingPerWorkedDayDefinitionValues,
  RawDefinitionStepValues,
}
